import React from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { normalizeMarkdown, parseTextFromMarkDown } from './utils';

const Container = styled.div`
  width: 100%;
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.6;
  font-family: var(--font-regular);
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  z-index: 9;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--text-primary);
    font-family: var(--font-bold);
    line-height: 1.3;
    margin: 1em 0 0.5em 0;
  }

  h1 {
    font-size: 22px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 16px;
  }
  h5 {
    font-size: 15px;
  }
  h6 {
    font-size: 14px;
  }

  p {
    font-size: 14px;
    margin: 0 0 1em 0;
  }

  a {
    color: var(--dark-blue);
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }

  strong {
    font-family: var(--font-bold);
  }

  ul,
  ol {
    margin: 0 0 1em 0;
    padding-left: 1.5em;
    list-style-position: outside;
  }

  li {
    margin-bottom: 0.5em;
  }
`;

interface RecommendationMarkdownProps {
  variant?: 'html' | 'plain';
  children: string;
}

const RecommendationMarkdown = ({ children, variant = 'html' }: RecommendationMarkdownProps) => {
  const normalized = normalizeMarkdown(children);

  if (variant === 'plain') {
    return <Container>{parseTextFromMarkDown(children)}</Container>;
  }

  return (
    <Container>
      <Markdown
        urlTransform={(url: string) => {
          const urlParts = url.split(':');
          if (urlParts.length !== 2) {
            return url;
          }
          switch (urlParts[0]) {
            case 'community':
              return `/community/${urlParts[1]}`;
            default:
              return url;
          }
        }}
        components={{
          a: ({ node, children, href, ...props }) => {
            return (
              <Link to={String(href)} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </Link>
            );
          },
          h1: ({ children }) => <h1>{children}</h1>,
          h2: ({ children }) => <h2>{children}</h2>,
          h3: ({ children }) => <h3>{children}</h3>,
          h4: ({ children }) => <h4>{children}</h4>,
          h5: ({ children }) => <h5>{children}</h5>,
          h6: ({ children }) => <h6>{children}</h6>,
          p: ({ children }) => <p>{children}</p>,
          ul: ({ children }) => <ul>{children}</ul>,
          ol: ({ children }) => <ol>{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong>{children}</strong>,
        }}
      >
        {normalized}
      </Markdown>
    </Container>
  );
};

export default RecommendationMarkdown;
